
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useUserRoles } from "@/hooks/useUserRoles";
import { ApplicationCard } from "./ApplicationCard";
import { DeleteApplicationDialog } from "./DeleteApplicationDialog";
import { cn } from "@/lib/utils";

interface Application {
  id: string;
  title: string;
  content: string | null;
  status: string | null;
  submitted_at: string;
  team_id: string;
}

interface ApplicationListProps {
  applications: Application[];
  isLoading: boolean;
  status: string | null;
  onApplicationDeleted?: () => void;
  isAdmin?: boolean;
}

export const ApplicationList = ({ 
  applications, 
  isLoading, 
  status, 
  onApplicationDeleted,
  isAdmin = false 
}: ApplicationListProps) => {
  const { toast } = useToast();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [applicationToDelete, setApplicationToDelete] = useState<string | null>(null);
  const { isAdmin: userIsAdmin } = useUserRoles();

  const filteredApplications = status === "all" 
    ? applications.filter(application => application.status !== "pending") 
    : applications?.filter(application => application.status === status);

  const handleDeleteClick = (id: string) => {
    setApplicationToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!applicationToDelete) return;

    try {
      const { error } = await supabase
        .from('applications')
        .delete()
        .eq('id', applicationToDelete);

      if (error) {
        throw error;
      }

      toast({
        title: "Application deleted",
        description: "The application has been successfully deleted.",
      });

      setDeleteDialogOpen(false);
      setApplicationToDelete(null);

      if (onApplicationDeleted) {
        onApplicationDeleted();
      }
    } catch (error) {
      console.error("Error deleting application:", error);
      toast({
        title: "Error",
        description: "Failed to delete the application. Please try again.",
        variant: "destructive",
      });
    }
  };

  const canDelete = isAdmin && userIsAdmin;
  const canEdit = isAdmin && userIsAdmin;

  return (
    <>
      <Card className={cn(
        "relative overflow-hidden rounded-xl border",
        "bg-gradient-to-br from-slate-50/80 to-slate-100/60",
        "border-slate-200/40"
      )}>
        {/* Decorative background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 right-0 w-8 h-8 bg-white rounded-full -translate-y-4 translate-x-4"></div>
          <div className="absolute bottom-0 left-0 w-6 h-6 bg-white rounded-full translate-y-3 -translate-x-3"></div>
        </div>
        
        <CardContent className="relative p-6">
          {isLoading ? (
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Skeleton key={i} className="h-52 w-full" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
              {filteredApplications.map((application) => (
                <ApplicationCard
                  key={application.id}
                  application={application}
                  canEdit={canEdit}
                  canDelete={canDelete}
                  onDeleteClick={handleDeleteClick}
                />
              ))}
              
              {filteredApplications.length === 0 && (
                <div className="col-span-full text-center py-8 text-slate-600/80">
                  No applications found
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <DeleteApplicationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDeleteConfirm}
      />
    </>
  );
};
